import React, { useCallback, useEffect, useMemo, useState } from "react";
import { appAlertsApi, usersApi, productsApi } from "../../services/api.service";
import { T, SectionLabel, Field, Input, Select, Btn } from "../shared/UI";
import { onlyOnAgents } from "../../utils/normalizers";

export default function AppAlertsView() {
  const today = new Date().toISOString().slice(0, 10);
  const [rows, setRows] = useState([]);
  const [agents, setAgents] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    agentId: "",
    agentName: "",
    productId: "",
    productName: "",
    issue: "",
    fromDate: today,
    toDate: today,
    search: "",
  });

  const agentOptions = useMemo(
    () => agents.map(a => ({ id: a.id, name: a.fullName || a.name || a.mobile })),
    [agents]
  );
  const productOptions = useMemo(
    () => products.map(p => ({ id: p.id, name: p.name })),
    [products]
  );

  const loadAgents = useCallback(async () => {
    try {
      const res = await usersApi.agentsMeta();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setAgents(onlyOnAgents(raw));
    } catch {
      setAgents([]);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      setProducts(raw);
    } catch {
      setProducts([]);
    }
  }, []);

  const loadAlerts = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        limit: meta.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== "" && v != null)
        ),
      };
      const res = await appAlertsApi.list(params);
      const raw = Array.isArray(res.data?.data) ? res.data.data : [];
      const metaRes = res.data?.meta || { page, limit: meta.limit, total: raw.length, totalPages: 1 };
      setRows(raw);
      setMeta({
        page: metaRes.page || page,
        limit: metaRes.limit || meta.limit,
        total: metaRes.total || raw.length,
        totalPages: metaRes.totalPages || 1,
      });
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load app alerts";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filters, meta.limit]);

  const exportCsv = () => {
    setExporting(true);
    setError(null);
    try {
      if (!rows.length) return;

      const headers = ["Agent", "Product", "Issue", "Details", "Timestamp"];
      const escape = (val) => {
        const s = String(val ?? "");
        if (s.includes(",") || s.includes("\"") || s.includes("\n")) return `"${s.replace(/"/g, "\"\"")}"`;
        return s;
      };

      const lines = [
        headers.join(","),
        ...rows.map(r => ([
          r.agentName || r.agentId || "",
          r.productName || "",
          r.issue || "",
          r.details || "",
          r.timestamp || "",
        ].map(escape).join(","))),
      ];

      const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `app-alerts-page-${meta.page}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err.message || "Export failed";
      setError(msg);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => { loadAgents(); }, [loadAgents]);
  useEffect(() => { loadProducts(); }, [loadProducts]);
  useEffect(() => { loadAlerts(1); }, [loadAlerts]);

  return (
    <div style={styles.wrap}>
      <div style={styles.toolbar}>
        <SectionLabel>APP ALERTS</SectionLabel>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={() => loadAlerts(1)}>Apply</Btn>
          <Btn variant="outline" onClick={exportCsv} disabled={!rows.length || exporting}>
            {exporting ? "Exporting…" : "Export CSV"}
          </Btn>
        </div>
      </div>

      <div style={styles.filters}>
        <Field label="AGENT">
          <Select value={filters.agentId} onChange={e => setFilters(f => ({ ...f, agentId: e.target.value }))}>
            <option value="">All</option>
            {agentOptions.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Select>
        </Field>
        <Field label="AGENT NAME">
          <Input value={filters.agentName} onChange={e => setFilters(f => ({ ...f, agentName: e.target.value }))} />
        </Field>
        <Field label="PRODUCT">
          <Select value={filters.productId} onChange={e => setFilters(f => ({ ...f, productId: e.target.value }))}>
            <option value="">All</option>
            {productOptions.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
        </Field>
        <Field label="PRODUCT NAME">
          <Input value={filters.productName} onChange={e => setFilters(f => ({ ...f, productName: e.target.value }))} />
        </Field>
        <Field label="ISSUE">
          <Input value={filters.issue} onChange={e => setFilters(f => ({ ...f, issue: e.target.value }))} />
        </Field>
        <Field label="FROM DATE">
          <Input type="date" value={filters.fromDate} onChange={e => setFilters(f => ({ ...f, fromDate: e.target.value }))} />
        </Field>
        <Field label="TO DATE">
          <Input type="date" value={filters.toDate} onChange={e => setFilters(f => ({ ...f, toDate: e.target.value }))} />
        </Field>
        <Field label="SEARCH">
          <Input value={filters.search} onChange={e => setFilters(f => ({ ...f, search: e.target.value }))} placeholder="issue/details" />
        </Field>
      </div>

      {loading && <div style={styles.info}>Loading alerts…</div>}
      {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              {["Agent","Product","Issue","Details","Timestamp"].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} style={styles.tr}>
                <td style={styles.td}>
                  <div style={{ fontWeight: 600 }}>{r.agentName || r.agentId}</div>
                </td>
                <td style={styles.tdM}>{r.productName || "—"}</td>
                <td style={styles.tdM}>{r.issue}</td>
                <td style={styles.td}>{r.details}</td>
                <td style={styles.tdM}>{r.timestamp}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr><td style={styles.empty} colSpan={5}>No alerts found.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={styles.pagination}>
        <Btn size="sm" variant="ghost" disabled={meta.page <= 1} onClick={() => loadAlerts(meta.page - 1)}>Prev</Btn>
        <span style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</span>
        <Btn size="sm" variant="ghost" disabled={meta.page >= meta.totalPages} onClick={() => loadAlerts(meta.page + 1)}>Next</Btn>
      </div>
    </div>
  );
}

const styles = {
  wrap: { flex:1, overflowY:"auto", padding:20, display:"flex", flexDirection:"column", gap:16 },
  toolbar: { display:"flex", alignItems:"center", gap:12, flexWrap:"wrap" },
  filters: { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(180px,1fr))", gap:12, background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12 },
  info: { fontSize:12, color:T.muted, padding:"6px 4px" },
  tableWrap: { background:T.s2, border:`1px solid ${T.border}`, borderRadius:12, padding:12, overflowX:"auto" },
  table: { width:"100%", borderCollapse:"collapse" },
  th: { textAlign:"left", fontFamily:T.fontMono, fontSize:9, letterSpacing:"1.5px", color:T.muted, padding:"6px 10px", borderBottom:`1px solid ${T.border}`, textTransform:"uppercase", whiteSpace:"nowrap" },
  tr: { borderBottom:`1px solid ${T.border}` },
  td: { padding:"8px 10px", fontSize:12, color:T.text, verticalAlign:"middle", whiteSpace:"nowrap" },
  tdM: { padding:"8px 10px", fontSize:11, color:T.muted, verticalAlign:"middle", whiteSpace:"nowrap" },
  empty: { padding:"16px 10px", fontSize:12, color:T.muted, textAlign:"center" },
  pagination: { display:"flex", alignItems:"center", justifyContent:"space-between" },
  pageLabel: { fontSize:11, color:T.muted, fontFamily:T.fontMono },
};
